import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

/**
 * Visual builder for the common shape of a workflow output mapping —
 * a JSONata object literal that composes the run output from a set of
 * node outputs:
 *
 *     { "answer": $."llm-agent-1".text, "tags": $."classify".tags }
 *
 * Rows: each row is (output key, JSONata expression). The expression
 * field has an "Insert node ref" dropdown that appends `$."nodeKey"` for
 * the picked node — saving the user from remembering the exact node key
 * and the quoting rules (JSONata identifiers with `-` need backtick or
 * double-quote escaping; `$."…"` always works).
 *
 * For mappings the builder can't parse (anything that isn't a flat
 * `{ "k": expr, … }` object literal — e.g. computed expressions, $sum,
 * conditionals), the component auto-flips to Raw mode and keeps the
 * textarea source of truth. Toggling back to Builder reparses.
 */

const cardStyle: CSSProperties = {
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--surface-1, var(--surface-2))',
};

const rowWrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: 'var(--sp-2)',
  borderBottom: '1px solid var(--border-subtle)',
};

const rowTop: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: 6,
  alignItems: 'center',
};

const rowBottom: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 120px',
  gap: 6,
  alignItems: 'center',
};

const smallBtn: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border-subtle)',
  color: 'var(--text-secondary)',
  borderRadius: 4,
  padding: '2px 8px',
  fontSize: 11,
  cursor: 'pointer',
  height: 26,
};

const monoInput: CSSProperties = {
  fontFamily: 'var(--font-mono, monospace)',
  fontSize: 12,
  padding: '4px 6px',
  height: 26,
};

// ─── Parser / serializer for the supported shape ────────────────────────────

interface IPair {
  key: string;
  expr: string;
}

/**
 * Parse a JSONata expression as a flat object literal `{ "k": expr, ... }`.
 * Returns `null` when the input isn't that shape — caller falls back to raw
 * editing. Handles strings, nested braces/parens/brackets, and escaped
 * characters inside strings.
 */
export function parseFlatObjectLiteral(text: string): IPair[] | null {
  const src = text.trim();
  if (src === '') return [];
  if (!src.startsWith('{') || !src.endsWith('}')) return null;
  const body = src.slice(1, -1).trim();
  if (body === '') return [];

  const segments: string[] = [];
  let depth = 0;
  let inStr: '"' | "'" | '`' | null = null;
  let escape = false;
  let buf = '';
  for (const ch of body) {
    if (escape) {
      buf += ch;
      escape = false;
      continue;
    }
    if (inStr) {
      buf += ch;
      if (ch === '\\') escape = true;
      else if (ch === inStr) inStr = null;
      continue;
    }
    if (ch === '"' || ch === "'" || ch === '`') {
      inStr = ch;
      buf += ch;
      continue;
    }
    if (ch === '{' || ch === '(' || ch === '[') depth++;
    else if (ch === '}' || ch === ')' || ch === ']') depth--;
    if (ch === ',' && depth === 0) {
      segments.push(buf);
      buf = '';
      continue;
    }
    buf += ch;
  }
  if (buf.trim() !== '') segments.push(buf);
  if (inStr || depth !== 0) return null;

  const pairs: IPair[] = [];
  for (const seg of segments) {
    const pair = splitKeyExpr(seg);
    if (!pair) return null;
    pairs.push(pair);
  }
  return pairs;
}

function splitKeyExpr(seg: string): IPair | null {
  // Find the top-level colon. The key portion can be a string literal or
  // a bare identifier. We only need to skip past a leading quoted string.
  const trimmed = seg.trim();
  let keyEnd = -1;
  if (trimmed.startsWith('"') || trimmed.startsWith("'")) {
    const quote = trimmed[0];
    let i = 1;
    let escape = false;
    for (; i < trimmed.length; i++) {
      const c = trimmed[i];
      if (escape) {
        escape = false;
        continue;
      }
      if (c === '\\') {
        escape = true;
        continue;
      }
      if (c === quote) break;
    }
    if (i >= trimmed.length) return null;
    keyEnd = i + 1; // past closing quote
  } else {
    keyEnd = trimmed.indexOf(':');
    if (keyEnd < 0) return null;
  }
  const colon = trimmed.indexOf(':', keyEnd);
  if (colon < 0) return null;
  const keyRaw = trimmed.slice(0, colon).trim();
  const expr = trimmed.slice(colon + 1).trim();
  const key = unquote(keyRaw);
  if (key === null) return null;
  return { key, expr };
}

function unquote(raw: string): string | null {
  if ((raw.startsWith('"') && raw.endsWith('"')) || (raw.startsWith("'") && raw.endsWith("'"))) {
    try {
      // Lean on JSON.parse for string-escape semantics. JSONata also accepts
      // single quotes; normalize first.
      const normalized = raw.startsWith("'") ? `"${raw.slice(1, -1).replace(/"/g, '\\"')}"` : raw;
      return JSON.parse(normalized) as string;
    } catch {
      return null;
    }
  }
  // Bare identifier
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(raw)) return raw;
  return null;
}

export function serializePairs(pairs: IPair[]): string {
  if (pairs.length === 0) return '';
  const inner = pairs
    .filter((p) => p.key.trim() !== '')
    .map((p) => `${JSON.stringify(p.key)}: ${p.expr || 'null'}`)
    .join(', ');
  return `{ ${inner} }`;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function JsonataMappingBuilder({
  value,
  onChange,
  availableNodeKeys,
}: {
  /** Current JSONata expression text. Empty string ⇒ no mapping. */
  value: string;
  onChange: (next: string) => void;
  /** Node keys the user can pick from for the "Insert node ref" dropdown. */
  availableNodeKeys: string[];
}) {
  const parsed = useMemo(() => parseFlatObjectLiteral(value), [value]);
  const isParseable = parsed !== null;

  const [mode, setMode] = useState<'builder' | 'raw'>(isParseable ? 'builder' : 'raw');
  const [rawText, setRawText] = useState(value);
  const [rawError, setRawError] = useState<string | null>(null);

  // If the parent value changes from underneath us (e.g. server refetch),
  // re-sync the raw textarea so toggling to Raw shows the new contents.
  useEffect(() => {
    setRawText(value);
    setRawError(null);
  }, [value]);

  // If the current value is not a flat object literal, stay in raw mode —
  // builder can't represent it without data loss.
  useEffect(() => {
    if (!isParseable && mode === 'builder') setMode('raw');
  }, [isParseable, mode]);

  return (
    <div style={cardStyle}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '6px var(--sp-2)',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'var(--surface-2)',
        }}
      >
        <div style={{ display: 'flex', gap: 4 }}>
          <ModeToggle
            label="Builder"
            active={mode === 'builder'}
            disabled={!isParseable}
            title={
              isParseable
                ? undefined
                : 'Builder only supports flat { "key": expr, … } mappings — edit raw or simplify.'
            }
            onClick={() => setMode('builder')}
          />
          <ModeToggle label="Raw JSONata" active={mode === 'raw'} onClick={() => setMode('raw')} />
        </div>
        <button
          type="button"
          style={{ ...smallBtn, borderColor: 'transparent' }}
          onClick={() => onChange('')}
          title="Clear mapping (fall back to last-terminal-node-wins)"
        >
          Clear
        </button>
      </div>

      {mode === 'builder' && parsed ? (
        <BuilderRows
          pairs={parsed}
          availableNodeKeys={availableNodeKeys}
          onChange={(next) => onChange(serializePairs(next))}
        />
      ) : (
        <div style={{ padding: 'var(--sp-2)' }}>
          <textarea
            className="input"
            rows={5}
            style={{ ...monoInput, height: 'auto', width: '100%' }}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
          />
          <div
            style={{
              display: 'flex',
              gap: 'var(--sp-2)',
              alignItems: 'center',
              marginTop: 'var(--sp-2)',
            }}
          >
            <button
              type="button"
              style={smallBtn}
              onClick={() => {
                onChange(rawText);
                setRawError(null);
                // If the new text is a flat object literal, hop back to builder.
                if (parseFlatObjectLiteral(rawText) !== null) setMode('builder');
              }}
            >
              Apply
            </button>
            {rawError ? (
              <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                {rawError}
              </span>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function ModeToggle({
  label,
  active,
  onClick,
  disabled,
  title,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        background: active ? 'var(--surface)' : 'transparent',
        color: disabled
          ? 'var(--text-muted)'
          : active
            ? 'var(--text-primary)'
            : 'var(--text-secondary)',
        border: '1px solid',
        borderColor: active ? 'var(--border-subtle)' : 'transparent',
        borderRadius: 4,
        padding: '2px 8px',
        fontSize: 11,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {label}
    </button>
  );
}

function BuilderRows({
  pairs,
  availableNodeKeys,
  onChange,
}: {
  pairs: IPair[];
  availableNodeKeys: string[];
  onChange: (next: IPair[]) => void;
}) {
  const update = (i: number, patch: Partial<IPair>) => {
    const current = pairs[i];
    if (!current) return;
    const next = pairs.slice();
    next[i] = { ...current, ...patch };
    onChange(next);
  };
  const remove = (i: number) => onChange(pairs.filter((_, j) => j !== i));
  // Seed a unique non-empty key on add — `serializePairs` filters empty
  // keys (the JSONata object literal can't have a "" key), so an
  // empty-key row would round-trip to nothing and the row would vanish
  // before the user could type. `key`, `key2`, … stays visible until
  // renamed.
  const add = () => {
    const taken = new Set(pairs.map((p) => p.key));
    let key = 'key';
    for (let i = 2; taken.has(key); i++) key = `key${i}`;
    onChange([...pairs, { key, expr: '' }]);
  };

  return (
    <div>
      {pairs.length === 0 ? (
        <div style={{ padding: 'var(--sp-3)' }}>
          <div
            className="t-xs"
            style={{ color: 'var(--text-secondary)', marginBottom: 'var(--sp-2)' }}
          >
            No output keys yet — add one to start composing the workflow output.
          </div>
          <button type="button" style={smallBtn} onClick={add}>
            + Add output key
          </button>
        </div>
      ) : (
        <>
          {pairs.map((p, i) => (
            <PairRow
              key={i}
              pair={p}
              availableNodeKeys={availableNodeKeys}
              onChange={(patch) => update(i, patch)}
              onRemove={() => remove(i)}
            />
          ))}
          <div style={{ padding: 'var(--sp-2)' }}>
            <button type="button" style={smallBtn} onClick={add}>
              + Add output key
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function PairRow({
  pair,
  availableNodeKeys,
  onChange,
  onRemove,
}: {
  pair: IPair;
  availableNodeKeys: string[];
  onChange: (patch: Partial<IPair>) => void;
  onRemove: () => void;
}) {
  const exprRef = useRef<HTMLInputElement | null>(null);
  const [keyDraft, setKeyDraft] = useState(pair.key);
  const [exprDraft, setExprDraft] = useState(pair.expr);
  useEffect(() => setKeyDraft(pair.key), [pair.key]);
  useEffect(() => setExprDraft(pair.expr), [pair.expr]);

  function insertNodeRef(nodeKey: string) {
    if (!nodeKey) return;
    const snippet = `$.${JSON.stringify(nodeKey)}`;
    // Clear the existing expression and replace it with the node reference.
    setExprDraft(snippet);
    onChange({ expr: snippet });
    const el = exprRef.current;
    if (el) {
      queueMicrotask(() => {
        el.focus();
        el.setSelectionRange(snippet.length, snippet.length);
      });
    }
  }

  return (
    <div style={rowWrap}>
      <div style={rowTop}>
        <input
          className="input"
          value={keyDraft}
          onChange={(e) => setKeyDraft(e.target.value)}
          onBlur={() => {
            if (keyDraft !== pair.key) onChange({ key: keyDraft });
          }}
          placeholder="output key"
          spellCheck={false}
          style={{ ...monoInput, minWidth: 0 }}
        />
        <button type="button" style={smallBtn} onClick={onRemove} title="Remove row">
          ×
        </button>
      </div>
      <div style={rowBottom}>
        <input
          ref={exprRef}
          className="input"
          value={exprDraft}
          onChange={(e) => setExprDraft(e.target.value)}
          onBlur={() => {
            if (exprDraft !== pair.expr) onChange({ expr: exprDraft });
          }}
          placeholder='$."node-key".path'
          spellCheck={false}
          style={{ ...monoInput, minWidth: 0 }}
        />
        <select
          className="select-native"
          value=""
          onChange={(e) => {
            insertNodeRef(e.target.value);
            e.currentTarget.value = '';
          }}
          title="Insert node reference"
          style={{ fontSize: 11, height: 26, padding: '2px 4px', minWidth: 0 }}
        >
          <option value="">Insert node ref…</option>
          {availableNodeKeys.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
